import { derived } from 'svelte/store';
import { persisted } from 'svelte-persisted-store';
import * as devalue from 'devalue';
import { v4 as uuidv4 } from 'uuid';

import { activeDomain } from './domain-settings';
import { userStore } from './user';

/**
 * Creates a custom store for notifications that can only be modified through specific methods.
 * @returns {{ subscribe: import('svelte/store').Readable<any[]>, addNotification: (notification: { message: string, domain: string }) => void, clearOldNotifications: () => void }}
 */
const createNotificationStore = () => {
  const { subscribe, update } = persisted('notifications', [], { serializer: devalue });

  return {
    subscribe,
    /**
     * Adds a new notification to the store.
     * @param {{ message: string, domain: string }} notification - The notification object to add.
     */
    addNotification: (notification) => {
      update((notifications) => {
        const now = new Date();
        const timestamp = notification.utcts || now;

        const latestNotifications = [
          {
            ...notification,
            id: uuidv4(),
            unread: true,
            timestamp: timestamp
          },
          ...notifications
        ];

        // Keep only the latest 50 notifications (reduced from 100) and remove notifications older than 7 days
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const filteredNotifications = latestNotifications
          .filter((n) => new Date(n.timestamp || n.utcts || 0) > sevenDaysAgo)
          .slice(0, 50);

        return filteredNotifications;
      });
    },
    /**
     * Marks all notifications as viewed/read.
     */
    viewNotifications: () => {
      update((notifications) => {
        return notifications.map((n) => {
          if (n.unread) {
            return { ...n, unread: false };
          }

          return n;
        });
      });
    },
    /**
     * Removes notifications older than specified days and limits total count
     */
    clearOldNotifications: () => {
      update((notifications) => {
        const now = new Date();
        const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);

        // Keep only notifications from last 3 days and limit to 25 items
        return notifications.filter((n) => new Date(n.timestamp || n.utcts || 0) > threeDaysAgo).slice(0, 25);
      });
    }
  };
};

/**
 * Store for managing notifications, with controlled modification through addNotification method.
 * @type {{ subscribe: import('svelte/store').Readable<any[]>, addNotification: (notification: { message: string, domain: string }) => void, clearOldNotifications: () => void }}
 */
export const notifications = createNotificationStore();

// Periodically clean up old notifications every 10 minutes
let cleanupInterval = null;
if (typeof window !== 'undefined') {
  cleanupInterval = setInterval(
    () => {
      notifications.clearOldNotifications();
    },
    10 * 60 * 1000
  );

  // Clean up interval when page is unloaded
  window.addEventListener('beforeunload', () => {
    if (cleanupInterval) {
      clearInterval(cleanupInterval);
      cleanupInterval = null;
    }
  });
}

/**
 * Derived store for notifications status based on the user's current settings.
 * @type {import('svelte/store').Readable<boolean>}
 */
export const desktopNotificationsEnabled = derived([userStore], ([$userStore]) => {
  return (
    $userStore?.settings?.enablesales &&
    ($userStore?.settings?.desktopnotifyadclick ||
      $userStore?.settings?.desktopnotifyattachmentdownload ||
      $userStore?.settings?.desktopnotifyattachmentview ||
      $userStore?.settings?.desktopnotifybeaconview ||
      $userStore?.settings?.desktopnotifylinkclick ||
      $userStore?.settings?.desktopnotifymyvideoactivity ||
      $userStore?.settings?.desktopnotifywebsitevisit)
  );
});

/**
 * Derived store for notifications status based on the user's current settings.
 * @type {import('svelte/store').Readable<boolean>}
 */
export const pushNotificationsEnabled = derived([userStore], ([$userStore]) => {
  return (
    $userStore?.settings?.enablesales &&
    ($userStore?.settings?.pushnotifyadclick ||
      $userStore?.settings?.pushnotifyattachmentdownload ||
      $userStore?.settings?.pushnotifyattachmentview ||
      $userStore?.settings?.pushnotifybeaconview ||
      $userStore?.settings?.pushnotifylinkclick ||
      $userStore?.settings?.pushnotifymyvideoactivity ||
      $userStore?.settings?.pushnotifywebsitevisit)
  );
});

/**
 * Derived store for filtered notifications based on the user's current domain.
 * @type {import('svelte/store').Readable<any[]>}
 */
export const activeNotifications = derived([notifications, activeDomain], ([$notifications, $activeDomain]) => {
  if (!$activeDomain) return $notifications;
  return $notifications.filter((notification) => notification?.domain === $activeDomain);
});

/**
 * Derived store for filtered notifications based on the unread status.
 * @type {import('svelte/store').Readable<any[]>}
 */
export const unreadNotifications = derived([notifications, activeDomain], ([$notifications, $activeDomain]) => {
  if (!$activeDomain) return $notifications.filter((notification) => notification.unread);
  return $notifications.filter((notification) => {
    return notification?.domain === $activeDomain && notification?.unread;
  });
});
